import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Container,
  Paper,
  IconButton,
  InputBase,
  MenuItem,
  FormControl,
  Grid,
  Button,
  Card,
  CardContent,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  Switch,
  Icon
} from '@mui/material';
import {
  Search as SearchIcon,
  TuneRounded as FilterIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Check as SuccessIcon,
  Add as AddIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import Layout from '../components/Layout';
import PlaidUpdateLink from '../components/PlaidUpdateLink';
import TransactionList from '../components/TransactionList';
// import CategorizerIssuesCard from '../components/CategorizerIssuesCard';
import BudgetCategoryMiniCard from '../components/BudgetCategoryMiniCard';
import { getTransactions, getConnectedAccounts } from '../services/transactionService';
import Logger from '../utils/logger';
import { StyledDialog, StyledTextField, StyledButton, StyledSelect } from '../components/common/StyledInputs';
import api from '../services/api';
import { calculateDailyAllowance } from '../utils/allowance';
import { dateOnlyPresetRange } from '../utils/dateUtils';
import { transactionsToCSV, downloadCSV, generateCSVFilename } from '../utils/csvExport';
import { useError } from '../contexts/ErrorContext';
import { useAlerts } from '../contexts/AlertContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { completeOnboardingStep } from '../services/onboardingService';
import CategoryReviewModal from '../components/CategoryReviewModal';
import BudgetReviewModal from '../components/BudgetReviewModal';
import RecurringPaymentEditModal from '../components/RecurringPaymentEditModal';
import AddExpenseModal from '../components/AddExpenseModal';
import ReimbursementMatchModal from '../components/ReimbursementMatchModal';
import TransactionReviewModal from '../components/TransactionReviewModal';
import BillMatchReviewModal from '../components/BillMatchReviewModal';
import { ALERT_ACTIONS, BUDGET_NAVIGATION_ACTIONS } from '../constants/alerts';
import { rebalanceAlertDestination, rebalanceCtaLabel } from '../utils/rebalanceAlertRoute';
import TransactionDetailModal from '../components/TransactionDetailModal';

const STORAGE_KEY = 'activityFilters';

// Removed unused getInitialFilters

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '20px',
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  transition: 'transform 80ms cubic-bezier(0, 0.3, 1, 0.5)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: 'none'
  },
  '&:active': {
    transform: 'scale(0.95)'
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  minWidth: 48,  // Keep minWidth to prevent squishing
  borderRadius: '50%',  // Make it perfectly circular
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
  '& svg': {
    color: (theme) => theme.palette.primary.main,
    fontSize: '24px'
  }
}));

const AlertTypeIcon = ({ type, metadata }) => {
  // Removed unused theme
  
  // If there's a custom icon in metadata, use that
  if (metadata?.icon) {
    return (
      <Icon 
        sx={{ 
          color: (theme) => theme.palette.primary.main,
          fontSize: '24px'
        }}
      >
        {metadata.icon}
      </Icon>
    );
  }

  // Otherwise fall back to type-based icons
  switch (type) {
    case 'warning':
      return <WarningIcon sx={{ color: (theme) => theme.palette.warning.main }} />;
    case 'error':
      return <ErrorIcon sx={{ color: (theme) => theme.palette.error.main }} />;
    case 'success':
      return <SuccessIcon sx={{ color: (theme) => theme.palette.success.main }} />;
    default:
      return <InfoIcon sx={{ color: (theme) => theme.palette.info.main }} />;
  }
};

// AlertSourceChip was unused; removed to satisfy lint

// Add the relative time formatter
const getRelativeTime = (date) => {
  const now = new Date();
  const diff = now - new Date(date);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ago`;
  } else if (hours > 0) {
    return `${hours}h ago`;
  } else if (minutes > 0) {
    return `${minutes}m ago`;
  } else {
    return 'just now';
  }
};

const AlertCard = ({ alert, onDismiss, setLegalConsentData, setLegalConsentModalOpen, setIsLoading, setCategoryReviewBudgetId, setCategoryReviewCategoryIds, setCategoryReviewModalOpen, setSelectedRecurringPaymentAlert, setRecurringPaymentEditModalOpen, setSelectedBudgetId, setBudgetReviewModalOpen, setSelectedReimbursementAlert, setReimbursementMatchModalOpen, setTransactionReviewIds, setTransactionReviewAlertId, setTransactionReviewModalOpen, setTransactionReviewType, setSelectedBillMatchAlert, setBillMatchReviewModalOpen, onOpenTransaction }) => {
  const { showError, showSuccess } = useError();
  const navigate = useNavigate();

  // Special-case rendering for budget category review alerts
  const titleSuggestsCategory = typeof alert?.title === 'string' && alert.title.toLowerCase().startsWith('review category');
  const isBudgetCategoryCard = (
    alert?.metadata?.renderAs === 'budgetCategoryCard' ||
    alert?.metadata?.action === 'reviewBudgetCategory' ||
    titleSuggestsCategory
  );

  if (isBudgetCategoryCard) {
    const name = alert?.metadata?.category?.name || alert?.metadata?.categoryName || 'Category';
    const budgeted = alert?.metadata?.category?.budgeted ?? 0;
    const spent = alert?.metadata?.category?.spent ?? 0;
    const budgetId = alert?.metadata?.budgetId;
    const categoryId = alert?.metadata?.categoryId;
    const route = alert?.metadata?.route || (budgetId ? `/budgets?budgetId=${budgetId}${categoryId ? `&categoryId=${categoryId}` : ''}` : '/budgets');

    return (
      <Box 
        sx={{ 
          mr: 2, 
          display: 'inline-block', 
          cursor: 'pointer',
          position: 'relative',
          transition: 'transform 80ms cubic-bezier(0, 0.3, 1, 0.5)',
          '&:hover': {
            transform: 'translateY(-4px)'
          },
          '&:active': {
            transform: 'scale(0.95)'
          }
        }} 
        onClick={() => navigate(route)}
      >
        {(alert?.isDismissible !== false) && (
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              try { onDismiss && alert._id && onDismiss([alert._id]); } catch (_) {}
            }}
            sx={{ 
              position: 'absolute',
              top: 6,
              right: 6,
              color: 'text.secondary',
              opacity: 0.5,
              '&:hover': { opacity: 1, backgroundColor: 'transparent' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        <BudgetCategoryMiniCard name={name} budgeted={budgeted} spent={spent} />
      </Box>
    );
  }

  const handleAction = async (action) => {
    let didSetLoading = false;

    try {
      // Referral reward card: open subscription page or dismiss
      if (alert?.metadata?.type === 'referral_reward_free_month') {
        if (action.value === 'view_details') {
          navigate('/account');
          if (alert._id) onDismiss([alert._id]);
          return;
        }
      }
      // Unlock Premium trial alert action
      if (alert?.metadata?.action === 'unlockPremiumTrial') {
        if (action.value === 'start_trial') {
          try {
            window.dispatchEvent(new CustomEvent('subscription:upgrade', { detail: { force: true } }));
          } catch (_) {}
          if (alert._id) onDismiss([alert._id]);
          return;
        }
      }
      if (alert.metadata?.action === 'confirmAccounts') {
        if (action.value === 'confirm') {
          didSetLoading = true;
          setIsLoading(true);
          Logger.info('Starting account confirmation process');

          try {
            // Use the consistent onboarding step completion pattern
            await api.post('/onboarding/complete', { step: 'accounts' });

            // Dismiss the alert after operation succeeds
            if (alert._id) {
              onDismiss([alert._id]);
            }

            Logger.info('Account confirmation complete');

            // Backend auto-initiates analysis; no client call needed
            // The backend triggers transaction analysis automatically after account confirmation
            // (see onboarding service changes). Keeping this client-side as a no-op avoids duplicate
            // runs and maintains idempotency across web and mobile.
            showSuccess('Your accounts have been confirmed and your initial budget is being prepared!');
          } catch (error) {
            Logger.error('Error during account confirmation:', error);
            // Revert the confirmation if it failed
            try {
              await api.post('/onboarding/revert', { step: 'accounts' });
            } catch (revertError) {
              Logger.error('Failed to revert account confirmation:', revertError);
            }
            throw error;
          }
        } else if (action.value === 'add_more') {
          // Store the current alert ID to prevent immediate reappearance
          const currentAlertId = alert._id;

          // Navigate to money page
          navigate('/money');

          // Dismiss the alert after navigation
          if (currentAlertId) {
            onDismiss([currentAlertId]);
          }
        }
      } else if (
        alert.metadata?.action === ALERT_ACTIONS.BUDGET_REBALANCE_RECOMMENDATION &&
        action.value === 'review'
      ) {
        const destination = rebalanceAlertDestination(alert.metadata);
        if (destination?.type === 'transaction') {
          await onOpenTransaction?.(destination.transactionId);
          return;
        }
        if (destination?.type === 'transactions') {
          navigate('/activity');
          return;
        }
        if (destination?.type === 'budgetReview') {
          setSelectedBudgetId(destination.budgetId);
          setBudgetReviewModalOpen(true);
          return;
        }
        navigate('/budgets');
        return;
      } else if (
        alert.metadata?.action === ALERT_ACTIONS.DAILY_CHECK_IN ||
        alert.metadata?.action === ALERT_ACTIONS.WEEKEND_SPENDING ||
        alert.metadata?.action === ALERT_ACTIONS.PAYDAY_OPTIMIZATION
      ) {
        if (action.value === 'review') {
          const budgetId = alert.metadata?.budgetId;
          if (budgetId) {
            setSelectedBudgetId(budgetId);
            setBudgetReviewModalOpen(true);
          } else {
            navigate('/budgets');
          }
        } else {
          Logger.warn('Unhandled alert action for DAILY_CHECK_IN/WEEKEND_SPENDING/PAYDAY_OPTIMIZATION', {
            action: alert.metadata?.action,
            value: action.value
          });
        }
        return;
      } else if (alert.metadata?.action === 'reviewBudget') {
        // Handle different action values for budget imbalance alerts.
        //
        // Platform-specific UX (intentional, see #279):
        //   - Web (here): URL-driven navigation to /budgets with budgetId + editMode params.
        //     The Budgets page reads `editMode` and surfaces a contextual banner explaining
        //     the action the user came here to take. This matches the rest of the web app's
        //     React Router-based flow.
        //   - Mobile: pushes a dedicated `/budget-review-edit` screen for the same action.
        //     See mobile/components/AlertsFeed.tsx for the equivalent handler.
        //
        // Both paths land the user in a focused, single-budget editing context with
        // contextual help — only the navigation primitive (URL vs. screen push) differs.
        if (BUDGET_NAVIGATION_ACTIONS.includes(action.value)) {
          // 'adjust' is for over-allocated budgets (Budget Imbalance)
          // 'allocate' is for under-allocated budgets (Unallocated Funds)
          const budgetId = alert.metadata?.budgetId;
          const editMode = action.value;
          if (budgetId) {
            navigate(`/budgets?budgetId=${budgetId}&editMode=${editMode}`);
          } else {
            // No specific budget targeted — fall back to the budgets index without
            // an editMode param so we don't pin allocate/adjust guidance to whatever
            // budget happens to be active.
            navigate('/budgets');
          }
          return;
        }
        // Default: use modal for general budget review
        setSelectedBudgetId(alert.metadata?.budgetId);
        setBudgetReviewModalOpen(true);
        return;
      } else if (alert.metadata?.action === 'family_assignment_needed') {
        // Web has no Buddy chat deck. Open the existing family review modal,
        // which loads GET /transactions/family/unattributed when no ids are set.
        setTransactionReviewIds([]);
        setTransactionReviewAlertId(alert._id);
        setTransactionReviewType('attribution');
        setTransactionReviewModalOpen(true);
        return;
      } else if (alert.metadata?.action === 'reviewLowConfidence' || alert.metadata?.action === 'provideFeedback') {
        // Open the transaction review modal for low-confidence categorizations or batch feedback
        const transactionIds = alert.metadata?.transactionIds || [];
        setTransactionReviewIds(transactionIds);
        setTransactionReviewAlertId(alert._id);
        setTransactionReviewType('low-confidence');
        setTransactionReviewModalOpen(true);
        return;
      } else if (alert.metadata?.action === 'reviewTransactionAttribution' || alert.transactionAttribution?.transactionId) {
        // Open the transaction review modal for family attribution
        // If transactionId is in transactionAttribution, use it; otherwise check metadata
        const transactionId = alert.transactionAttribution?.transactionId || alert.metadata?.transactionId;
        const transactionIds = transactionId ? [transactionId] : (alert.metadata?.transactionIds || []);
        setTransactionReviewIds(transactionIds);
        setTransactionReviewAlertId(alert._id);
        setTransactionReviewType('attribution');
        setTransactionReviewModalOpen(true);
        return;
      } else if (alert.metadata?.action === 'confirmRecurringPayment') {
        if (action.value === 'confirm') {
          // User confirms the detected recurring payment - just dismiss the alert
          if (alert._id && onDismiss) {
            await onDismiss([alert._id]);
          }
          showSuccess('Recurring payment confirmed!');
        } else if (action.value === 'update') {
          // User wants to update the amount - open edit modal
          setSelectedRecurringPaymentAlert(alert);
          setRecurringPaymentEditModalOpen(true);
        }
      } else if (alert.metadata?.action === 'reviewReimbursementRequest') {
        if (action.value === 'paid') {
          // User has already paid - show transaction selection modal
          setSelectedReimbursementAlert(alert);
          setReimbursementMatchModalOpen(true);
          return;
        } else if (action.value === 'not_paid') {
          // User hasn't paid yet - dismiss the request
          const { handleReimbursementRequest } = await import('../services/api');
          try {
            await handleReimbursementRequest(alert.metadata.reimbursementTransactionId, 'not_paid');
            if (alert._id && onDismiss) {
              await onDismiss([alert._id]);
            }
            showSuccess('Reimbursement request dismissed');
          } catch (error) {
            console.error('Error dismissing reimbursement request:', error);
            showError('Failed to dismiss reimbursement request');
          }
          return;
        }
      } else if ((alert.metadata?.action === 'retryBudgetInit' || alert.metadata?.action === 'retryBudgetCreation' || alert.metadata?.action === 'retryTransactionAnalysis') && action.value === 'retry') {
        didSetLoading = true;
        setIsLoading(true);
        Logger.info('Starting transaction analysis retry');

        try {
          await api.post('/onboarding/complete', {
            step: 'accounts',
            isRetry: true
          });

          // Dismiss the alert after operation succeeds
          if (alert._id) {
            onDismiss([alert._id]);
          }

          Logger.info('Transaction analysis retry complete');
          showSuccess('Transaction analysis started successfully!');
        } catch (error) {
          Logger.error('Error during transaction analysis retry:', error);
          showError('Failed to retry transaction analysis. Please try again.');
        } finally {
          if (didSetLoading) {
            setIsLoading(false);
          }
        }
      } else if (alert.metadata?.action === 'accept_legal_consent') {
        // Open legal consent modal
        setLegalConsentData({
          alertId: alert._id,
          needsTermsUpdate: alert.metadata?.needsTermsUpdate,
          needsPrivacyUpdate: alert.metadata?.needsPrivacyUpdate,
          requiredVersions: alert.metadata?.requiredVersions,
          currentVersions: alert.metadata?.currentVersions
        });
        setLegalConsentModalOpen(true);
        return;
      } else if (alert.metadata?.action === 'acceptFamilyInvitation') {
        if (action.value === 'accept_family_invitation') {
          try {
            const { acceptFamilyInvitation } = await import('../services/api');
            const token = alert.metadata?.invitationToken;
            if (!token) {
              showError('Invalid invitation token');
              return;
            }

            await acceptFamilyInvitation(token);
            
            // Dismiss the alert
            if (alert._id && onDismiss) {
              await onDismiss([alert._id]);
            }

            showSuccess('Family invitation accepted! You are now part of the family account.');
            
            // Reload page to refresh user state
            window.location.reload();
          } catch (err) {
            Logger.error('Error accepting family invitation:', err);
            showError(err?.response?.data?.error || 'Failed to accept invitation');
          }
        } else if (action.value === 'decline_family_invitation') {
          // Dismiss the invitation alert
          if (alert._id && onDismiss) {
            await onDismiss([alert._id]);
          }
          showSuccess('Family invitation declined');
        }
        return;
      } else if (alert.metadata?.action === 'reviewBillMatch') {
        if (action.value === 'review') {
          setSelectedBillMatchAlert(alert);
          setBillMatchReviewModalOpen(true);
          return;
        }
      }
    } catch (error) {
      Logger.error('Error handling alert action:', error);
      showError('An error occurred. Please try again.');
    } finally {
      if (didSetLoading) {
        setIsLoading(false);
      }
    }
  };


  return (
    <StyledCard sx={{ 
      minWidth: 300, 
      maxWidth: 300, 
      mr: 2, 
      minHeight: 280,
      position: 'relative',
      cursor: alert.metadata?.route ? 'pointer' : 'default',
      '&:hover': alert.metadata?.route ? {
        transform: 'translateY(-4px)',
        transition: 'transform 80ms cubic-bezier(0, 0.3, 1, 0.5)'
      } : {},
      '&:active': alert.metadata?.route ? {
        transform: 'scale(0.95)'
      } : {}
    }} onClick={(e) => {
      // Only navigate if we have a route and the click wasn't on the dismiss button
      if (alert.metadata?.route && !e.defaultPrevented) {
        navigate(alert.metadata.route);
      }
    }}>
      <CardContent sx={{ 
        p: 3, 
        height: '100%',
        pb: '3 !important'
      }}>
        {/* Top section with icon and close button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper>
              <AlertTypeIcon type={alert.type} metadata={alert.metadata} />
            </IconWrapper>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {alert.title}
            </Typography>
          </Box>
          {(alert.metadata?.dismissible || alert.isDismissible) && (
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDismiss([alert._id]);
              }}
              sx={{ 
                mt: -1, 
                mr: -1,
                color: 'text.secondary',
                opacity: 0.5,
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'transparent'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Content section */}
        <Box sx={{ pr: 0, pb: 4 }}> {/* Add padding bottom to prevent content from overlapping with time */}
          <Typography variant="body2" color="text.secondary">
            {alert.message}
          </Typography>
          
          {/* Action Buttons */}
          {(alert.metadata?.actions || alert.source === 'accounts') && (
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              mt: 2,
              justifyContent: 'flex-start'
            }}>
              {alert.source === 'accounts' && alert.metadata?.itemId ? (
                <PlaidUpdateLink
                  itemId={alert.metadata.itemId}
                  accountSelectionEnabled={!!alert.metadata?.accountSelectionEnabled}
                  onResolved={() => {
                    try { onDismiss && alert._id && onDismiss([alert._id]); } catch (e) {}
                  }}
                  buttonProps={{
                    sx: {
                      borderRadius: '50px',
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      backgroundColor: (theme) => theme.palette.primary.main,
                      color: 'white'
                    }
                  }}
                >
                  {alert.metadata?.accountSelectionEnabled ? 'Add Accounts' : 'Update'}
                </PlaidUpdateLink>
              ) : null}
              {alert.metadata?.actions?.map((action, index) => (
                <StyledButton
                  key={index}
                  variant={action.variant || (action.primary ? 'contained' : 'outlined')}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction({ value: action.value });
                  }}
                  startIcon={action.value === 'add_more' ? <AddIcon /> : <CheckIcon />}
                  sx={{ px: 2.5, minHeight: '28px' }}
                >
                  {rebalanceCtaLabel(alert.metadata) || action.label}
                </StyledButton>
              ))}
            </Box>
          )}
        </Box>

        {/* Time section - positioned relative to the card */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            opacity: 0.7,
            position: 'absolute',
            bottom: 24,
            right: 24,
            lineHeight: 1
          }}
        >
          {getRelativeTime(alert.createdAt)}
        </Typography>
      </CardContent>
    </StyledCard>
  );
};

const Activity = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const idsFromUrl = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      return params.get('ids') || '';
    } catch (e) {
      return '';
    }
  }, [location.search]);
  const [isLoading, setIsLoading] = useState(false);
  // const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  // Removed unused totalPages state
  const ITEMS_PER_PAGE = 30;
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  // const transactionListRef = useRef(null);
  const mainContainerRef = useRef(null);
  const authCheckRef = useRef(null);
  const hasInitialFetchRef = useRef(false);
  // Removed unused auto-scroll state
  const [dateFilter, setDateFilter] = useState('all');
  const [amountFilter, setAmountFilter] = useState('all');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [showTransfers, setShowTransfers] = useState(true);
  const [showUncategorized, setShowUncategorized] = useState(true);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  // Inclusive date-only keys — same helper as mobile Activity / day sheets (#1547)
  const getDateRange = useCallback((dateFilterValue) => {
    return dateOnlyPresetRange(dateFilterValue) || { startDate: null, endDate: null };
  }, []);

  const [appliedFilters, setAppliedFilters] = useState({
    dateFilter: 'all',
    amountFilter: 'all',
    minAmount: '',
    maxAmount: '',
    showTransfers: true,
    showUncategorized: true,
    selectedAccounts: [],
    selectedInstitutions: []
  });
  const { 
    alerts,
    loading: alertsLoading,
    dismiss,
    fetchAlerts
  } = useAlerts();
  const [budgetInsightsCache, setBudgetInsightsCache] = useState({}); // { [budgetId]: insights }
  const [budgetDetailsCache, setBudgetDetailsCache] = useState({}); // { [budgetId]: budget }
  const [enrichedAlerts, setEnrichedAlerts] = useState([]);
  const [allowance, setAllowance] = useState(0);
  const isChildUser = user?.family?.role === 'child';
  // const [selectedStatus, setSelectedStatus] = useState('all');
  // Remove unused theme and navigate if not needed here
  const { showError, showSuccess } = useError();
  const [error] = useState(null);

  // Legal consent modal state
  const [legalConsentModalOpen, setLegalConsentModalOpen] = useState(false);
  const [legalConsentData, setLegalConsentData] = useState(null);

  // Category review modal state (Issue #129)
  const [categoryReviewModalOpen, setCategoryReviewModalOpen] = useState(false);
  const [categoryReviewBudgetId, setCategoryReviewBudgetId] = useState(null);
  const [categoryReviewCategoryIds, setCategoryReviewCategoryIds] = useState([]);

  // Budget review modal state
  const [budgetReviewModalOpen, setBudgetReviewModalOpen] = useState(false);
  const [selectedBudgetId, setSelectedBudgetId] = useState(null);

  // Add expense modal state
  const [addExpenseModalOpen, setAddExpenseModalOpen] = useState(false);

  // Recurring payment edit modal state
  const [recurringPaymentEditModalOpen, setRecurringPaymentEditModalOpen] = useState(false);
  const [selectedRecurringPaymentAlert, setSelectedRecurringPaymentAlert] = useState(null);

  // Reimbursement match modal state
  const [reimbursementMatchModalOpen, setReimbursementMatchModalOpen] = useState(false);
  const [selectedReimbursementAlert, setSelectedReimbursementAlert] = useState(null);

  // Transaction review modal state (for low-confidence AI categorizations and family attribution)
  const [transactionReviewModalOpen, setTransactionReviewModalOpen] = useState(false);
  const [billMatchReviewModalOpen, setBillMatchReviewModalOpen] = useState(false);
  const [selectedBillMatchAlert, setSelectedBillMatchAlert] = useState(null);
  const [transactionReviewIds, setTransactionReviewIds] = useState([]);
  const [transactionReviewAlertId, setTransactionReviewAlertId] = useState(null);
  const [transactionReviewType, setTransactionReviewType] = useState('low-confidence');
  const [linkedTransaction, setLinkedTransaction] = useState(null);

  // Large-expense CTAs open this one transaction. The Activity list defaults
  // to uncategorized-only, which would hide a categorized charge, so this
  // fetch asks for the id with that filter off. A miss stays on the list.
  const openLinkedTransaction = useCallback(async (transactionId) => {
    try {
      const response = await getTransactions({
        page: 1,
        limit: 1,
        id: transactionId,
        showTransfers: 'true',
        showUncategorized: 'false',
      });
      const transaction = Array.isArray(response?.transactions) ? response.transactions[0] : null;
      if (transaction) {
        setLinkedTransaction(transaction);
        return;
      }
    } catch (error) {
      Logger.error('Failed to open large-expense transaction', error);
    }
    navigate('/activity');
  }, [navigate]);

  // Handle legal consent acceptance
  const handleAcceptLegalConsent = async () => {
    try {
      setIsLoading(true);

      // Read the live versions, as Expo and Swift do. The alert's copy can be
      // older than a version bump (#2512); it is only the fallback.
      const status = await api.get('/legal/status').then((r) => r.data).catch(() => null);
      const requiredVersions = status?.requiredVersions || legalConsentData?.requiredVersions;
      await api.post('/legal/consent', {
        termsVersion: requiredVersions?.terms,
        privacyVersion: requiredVersions?.privacy
      });

      // Dismiss the alert
      if (legalConsentData?.alertId) {
        dismiss([legalConsentData.alertId]);
      }

      // Close modal
      setLegalConsentModalOpen(false);
      setLegalConsentData(null);

      showSuccess('Legal consent accepted successfully!');
    } catch (error) {
      Logger.error('Error accepting legal consent:', error);
      showError('Failed to accept legal consent. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Stable key for filters to avoid deep object dep churn
  const appliedFiltersKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);
  const lastFetchKeyRef = useRef('');
  const isLoadingRef = useRef(false);
  const fetchAlertsRef = useRef(null);
  const fetchTransactionsRef = useRef(null);
  const fetchAccountsRef = useRef(null);
  const showErrorRef = useRef(null);

  // Set after declarations below

  // Define fetchers before effects and memoize to avoid dependency churn
  const fetchTransactions = useCallback((pageNum = 1, append = false, overrideFilters = null) => {
    if (authLoading) {
      Logger.debug('Skipping transaction fetch - auth still loading');
      return Promise.resolve();
    }

    try {
      const effectiveFilters = overrideFilters || appliedFilters;
      setIsLoading(true);
      Logger.debug('Fetching transactions', {
        page: pageNum,
        append
      });

      // Convert dateFilter preset to startDate/endDate
      const { startDate, endDate } = getDateRange(effectiveFilters.dateFilter);
      
      return getTransactions({
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        searchTerm,
        selectedAccounts: effectiveFilters.selectedAccounts,
        selectedInstitutions: effectiveFilters.selectedInstitutions,
        // Pass date filters to API
        startDate,
        endDate,
        // Pass transfer/uncategorized filters to API (backend expects string 'true'/'false')
        showTransfers: effectiveFilters.showTransfers === true ? 'true' : 'false',
        showUncategorized: effectiveFilters.showUncategorized === true ? 'true' : 'false',
        // Optional: narrow results to specific transaction IDs (used for low-confidence review gating)
        ids: idsFromUrl || undefined
      }).then(response => {
        const apiTransactions = response.transactions || [];
        const totalPages = response.pages || 1;

        // Apply local account/institution filter fallback
        const filteredByAccounts = (() => {
          const hasAccountFilter = Array.isArray(effectiveFilters.selectedAccounts) && effectiveFilters.selectedAccounts.length > 0;
          const hasInstitutionFilter = Array.isArray(effectiveFilters.selectedInstitutions) && effectiveFilters.selectedInstitutions.length > 0;
          if (!hasAccountFilter && !hasInstitutionFilter) return apiTransactions;

          const accountSet = new Set(effectiveFilters.selectedAccounts || []);
          const institutionSet = new Set(effectiveFilters.selectedInstitutions || []);

          // Blacklist semantics: selectedAccounts/Institutions are accounts to HIDE
          return apiTransactions.filter(tx => {
            const accId =
              tx.accountId ||
              tx.plaidAccountId ||
              tx.account?.id ||
              tx.account?.accountId ||
              tx.account?.plaidAccountId;
            const instId = tx.institutionId || tx.account?.institutionId;
            // Account is visible if NOT in the hidden set
            const accountVisible = hasAccountFilter ? !accountSet.has(accId) : true;
            const instVisible = hasInstitutionFilter ? !institutionSet.has(instId) : true;
            return accountVisible && instVisible;
          });
        })();

        setHasMore(pageNum < totalPages);
        if (append) {
          setTransactions(prev => [...prev, ...filteredByAccounts]);
        } else {
          setTransactions(filteredByAccounts);
        }
      }).catch(error => {
        Logger.error('Failed to fetch transactions', error);
        showError('Failed to fetch transactions');
      }).finally(() => {
        setIsLoading(false);
      });
    } catch (error) {
      Logger.error('Failed to fetch transactions', error);
      showError('Failed to fetch transactions');
      setIsLoading(false);
      return Promise.resolve();
    }
  }, [authLoading, searchTerm, showError, appliedFilters, getDateRange, idsFromUrl]);

  const fetchAccounts = useCallback(() => {
    if (authLoading) {
      Logger.debug('Skipping accounts fetch - auth still loading');
      return Promise.resolve();
    }

    try {
      setAccountsLoading(true);
      return getConnectedAccounts()
        .then(response => {
          // Unified endpoint returns flat array of accounts
          const flattenedAccounts = (response || []).map(account => ({
            id: account.accountId || account.id,
            name: account.displayName || account.name,
            mask: account.mask,
            institutionName: account.institution?.name || 'Other',
            institutionId: account.institution?.id || 'other',
            type: account.type,
            subtype: account.subtype,
            balance: account.balance
          }));
          setAccounts(flattenedAccounts);
        })
        .catch(error => {
          Logger.error('Failed to fetch accounts', error);
          showError('Failed to fetch accounts');
        })
        .finally(() => setAccountsLoading(false));
    } catch (error) {
      Logger.error('Failed to fetch accounts', error);
      showError('Failed to fetch accounts');
      setAccountsLoading(false);
      return Promise.resolve();
    }
  }, [authLoading, showError]);

  // Sync refs after definitions
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
  useEffect(() => { fetchAlertsRef.current = fetchAlerts; }, [fetchAlerts]);
  useEffect(() => { fetchTransactionsRef.current = fetchTransactions; }, [fetchTransactions]);
  useEffect(() => { fetchAccountsRef.current = fetchAccounts; }, [fetchAccounts]);
  useEffect(() => { showErrorRef.current = showError; }, [showError]);

  // Single effect to handle all transaction fetching
  useEffect(() => {
    // Skip if not authenticated or still loading auth
    if (!isAuthenticated || authLoading) {
      return;
    }

    // Skip if already loading
    if (isLoadingRef.current) {
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        Logger.debug('Fetching transactions', { page, searchTerm, filtersKey: appliedFiltersKey });

        // Fetch transactions and alerts in parallel
        await Promise.all([
          fetchTransactionsRef.current(1, false),
          fetchAlertsRef.current({
            page: 1,
            limit: ITEMS_PER_PAGE,
            status: ['pending', 'read']
          }),
          fetchAccountsRef.current()
        ]);

        // Mark initial fetch complete
        hasInitialFetchRef.current = true;
        // setIsInitialLoad(false);
      } catch (error) {
        Logger.error('Error fetching data:', error);
        if (showErrorRef.current) {
          showErrorRef.current('Failed to load data. Please try again.');
        }
      } finally {
        setIsLoading(false);
        // Record last fetch signature
        lastFetchKeyRef.current = `${searchTerm}|${appliedFiltersKey}|${idsFromUrl}`;
      }
    };

    // Debounce the fetch for search and filter changes
    const signature = `${searchTerm}|${appliedFiltersKey}|${idsFromUrl}`;
    if (hasInitialFetchRef.current && lastFetchKeyRef.current === signature) {
      return;
    }
    const timeoutId = setTimeout(() => { fetchData(); }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    isAuthenticated,
    authLoading,
    searchTerm,
    appliedFiltersKey,
    page,
    idsFromUrl
  ]);

  const fetchAllowance = useCallback(async () => {
    if (!isChildUser) {
      setAllowance(0);
      return;
    }
    try {
      const budgetsRes = await api.get('/budgets');
      const budgets = Array.isArray(budgetsRes?.data) ? budgetsRes.data : [];
      const total = await calculateDailyAllowance(budgets);
      setAllowance(total);
    } catch (e) {
      setAllowance(0);
    }
  }, [isChildUser]);

  // When search term changes (including cleared), reset pagination and refetch from page 1
  // Note: We intentionally exclude fetchTransactions from deps to avoid double-fetching
  // when appliedFilters change (since handleApplyFilters already calls fetchTransactions)
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setTransactions([]);
    fetchTransactionsRef.current(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => { fetchAllowance(); }, [fetchAllowance]);

  // Enrich alerts with live spend for budget category cards
  useEffect(() => {
    const enrich = async () => {
      try {
        const categoryAlerts = (alerts || []).filter(a => {
          const titleSuggestsCategory = typeof a?.title === 'string' && a.title.toLowerCase().startsWith('review category');
          const isFlagged = a?.metadata?.renderAs === 'budgetCategoryCard' || a?.metadata?.action === 'reviewBudgetCategory' || titleSuggestsCategory;
          return isFlagged && a?.metadata?.budgetId && a?.metadata?.categoryId;
        });
        if (categoryAlerts.length === 0) {
          setEnrichedAlerts(alerts || []);
          return;
        }

        const uniqueBudgetIds = Array.from(new Set(categoryAlerts.map(a => String(a.metadata.budgetId))));
        const budgetsToFetchInsights = uniqueBudgetIds.filter(bid => !budgetInsightsCache[bid]);
        const budgetsToFetchDetails = uniqueBudgetIds.filter(bid => !budgetDetailsCache[bid]);

        let insightsSource = budgetInsightsCache;
        if (budgetsToFetchInsights.length > 0) {
          const results = await Promise.allSettled(budgetsToFetchInsights.map(bid => api.get(`/budgets/${bid}/insights`)));
          const next = { ...budgetInsightsCache };
          results.forEach((res, idx) => {
            const bid = budgetsToFetchInsights[idx];
            if (res.status === 'fulfilled') {
              const payload = res.value?.data;
              next[bid] = (payload && payload.data) ? payload.data : payload;
            }
          });
          setBudgetInsightsCache(next);
          insightsSource = next;
        }

        let detailsSource = budgetDetailsCache;
        if (budgetsToFetchDetails.length > 0) {
          const results = await Promise.allSettled(budgetsToFetchDetails.map(bid => api.get(`/budgets/${bid}`)));
          const next = { ...budgetDetailsCache };
          results.forEach((res, idx) => {
            const bid = budgetsToFetchDetails[idx];
            if (res.status === 'fulfilled' && res.value?.data) next[bid] = res.value.data;
          });
          setBudgetDetailsCache(next);
          detailsSource = next;
        }

        const updated = (alerts || []).map(a => {
          const titleSuggestsCategory = typeof a?.title === 'string' && a.title.toLowerCase().startsWith('review category');
          const isCard = a?.metadata?.renderAs === 'budgetCategoryCard' || a?.metadata?.action === 'reviewBudgetCategory' || titleSuggestsCategory;
          if (!isCard || !a?.metadata?.budgetId || !a?.metadata?.categoryId) return a;
          const bid = String(a.metadata.budgetId);
          const cid = String(a.metadata.categoryId);
          const insights = insightsSource[bid] || {};
          const spentMap = insights?.categorySpent || {};
          const spent = Number(spentMap[cid] || 0);
          let categoryObj = a?.metadata?.category;
          if (!categoryObj) {
            const b = detailsSource[bid];
            const cat = b?.categories?.find(c => String(c._id) === cid);
            if (cat) {
              const totalBudget = Number(b?.totalBudget || 0);
              const budgeted = Number(cat.isPercentage ? (Number(cat.amount || 0) / 100) * totalBudget : Number(cat.amount || 0));
              categoryObj = { id: cat._id, name: cat.name, icon: cat.icon, budgeted, spent };
            }
          }
          return {
            ...a,
            metadata: {
              ...a.metadata,
              renderAs: 'budgetCategoryCard',
              category: {
                ...(categoryObj || a?.metadata?.category || {}),
                spent
              }
            }
          };
        });

        setEnrichedAlerts(updated);
      } catch (e) {
        Logger.error('Failed to enrich alerts on Activity page', e);
        setEnrichedAlerts(alerts || []);
      }
    };
    enrich();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts]);

  const formatAllowance = (value) => {
    if (!isFinite(value)) return '$0';
    if (Math.abs(value) < 0.005) return '$0';
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  // Remove other fetch-related effects

  // Keep the cleanup effect for authCheckRef
  useEffect(() => {
    const t = authCheckRef.current;
    return () => {
      if (t) {
        clearTimeout(t);
      }
    };
  }, []);

  // fetchTransactions and fetchAccounts defined above

  // Remove local filter state management
  // Removed unused handleFilterApply

  // Update the handleInstitutionToggle to use auth state
  const handleInstitutionToggle = (institutionAccounts, institutionId, checked) => {
    if (authLoading) {
      Logger.debug('Skipping institution toggle - auth still loading');
      return;
    }

    Logger.debug('Institution toggle:', {
      institutionId,
      checked,
      accountCount: institutionAccounts.length,
      accountIds: institutionAccounts.map(acc => acc.id)
    });

    // Get all account IDs for this institution
    const institutionAccountIds = institutionAccounts.map(acc => acc.id);

    // Locally update hidden accounts for this institution (blacklist semantics)
    // checked=true means "show" → remove from hidden list
    // checked=false means "hide" → add to hidden list
    setSelectedAccounts(prev => {
      const prevSet = new Set(prev || []);
      if (checked) {
        // User wants to SHOW → remove from hidden list
        institutionAccountIds.forEach(id => prevSet.delete(id));
      } else {
        // User wants to HIDE → add to hidden list
        institutionAccountIds.forEach(id => prevSet.add(id));
      }
      const next = Array.from(prevSet);
      return next;
    });
  };

  // Update the handleAccountToggle to use auth state
  const handleAccountToggle = (account, institutionAccounts, checked) => {
    if (authLoading) {
      Logger.debug('Skipping account toggle - auth still loading');
      return;
    }

    // Locally update hidden accounts (blacklist semantics)
    // checked=true means "show" → remove from hidden list
    // checked=false means "hide" → add to hidden list
    setSelectedAccounts(prev => {
      const next = checked
        ? (prev || []).filter(id => id !== account.id)  // Show: remove from hidden list
        : Array.from(new Set([...(prev || []), account.id]));  // Hide: add to hidden list

      return next;
    });
  };

  // Mock balances - replace with real data from your API
  // Removed unused balances mock

  // Removed unused BalanceCard component

  // Removed unused getDateRange

  // Add filterTransactions function
  const filterTransactions = useCallback((transactions, searchTerm) => {
    if (!searchTerm) return transactions;
    
    const searchLower = searchTerm.toLowerCase();
    return transactions.filter(transaction => {
      // Search in merchant name
      if (transaction.merchantName?.toLowerCase().includes(searchLower)) return true;
      
      // Search in description
      if (transaction.description?.toLowerCase().includes(searchLower)) return true;
      
      // Search in amount (convert to string for partial matches)
      const amount = Math.abs(transaction.amount).toFixed(2);
      if (amount.includes(searchTerm)) return true;
      
      // Search in category name
      if (transaction.budgetCategory?.name?.toLowerCase().includes(searchLower)) return true;
      
      return false;
    });
  }, []);

  // Removed standalone search effect - account/institution filter effect below handles both

  // Apply account/institution/amount filters AND search locally after transactions change
  useEffect(() => {
    // BLACKLIST semantics: selectedAccounts/selectedInstitutions are accounts to HIDE
    // Toggle OFF = add to list (hide), Toggle ON = remove from list (show)
    const hasAccountFilter = Array.isArray(appliedFilters.selectedAccounts) && appliedFilters.selectedAccounts.length > 0;
    const hasInstitutionFilter = Array.isArray(appliedFilters.selectedInstitutions) && appliedFilters.selectedInstitutions.length > 0;
    const hiddenAccountSet = new Set(appliedFilters.selectedAccounts || []);
    const hiddenInstitutionSet = new Set(appliedFilters.selectedInstitutions || []);

    // Parse amount filters (backend doesn't support these, so we apply client-side)
    const minAmountNum = appliedFilters.minAmount ? parseFloat(appliedFilters.minAmount) : null;
    const maxAmountNum = appliedFilters.maxAmount ? parseFloat(appliedFilters.maxAmount) : null;

    // Apply all local filters: account/institution blacklist + amount range
    const filtered = transactions.filter(tx => {
      const accId =
        tx.accountId ||
        tx.plaidAccountId ||
        tx.account?.id ||
        tx.account?.accountId ||
        tx.account?.plaidAccountId;
      const instId = tx.institutionId || tx.account?.institutionId;
      // Account is visible if NOT in the hidden set
      const accountVisible = hasAccountFilter ? !hiddenAccountSet.has(accId) : true;
      const instVisible = hasInstitutionFilter ? !hiddenInstitutionSet.has(instId) : true;
      
      // Amount filter (use absolute value for comparison since expenses are positive, income negative)
      const txAmount = Math.abs(tx.amount || 0);
      const amountVisible = (
        (minAmountNum === null || txAmount >= minAmountNum) &&
        (maxAmountNum === null || txAmount <= maxAmountNum)
      );
      
      return accountVisible && instVisible && amountVisible;
    });

    // Keep search filtering stacked on top
    if (!searchTerm) {
      setFilteredTransactions(filtered);
    } else {
      const filteredBySearch = filterTransactions(filtered, searchTerm);
      setFilteredTransactions(filteredBySearch);
    }
  }, [
    transactions,
    appliedFilters.selectedAccounts,
    appliedFilters.selectedInstitutions,
    appliedFilters.minAmount,
    appliedFilters.maxAmount,
    searchTerm,
    filterTransactions
  ]);

  // Removed unused isAuthStateStable

  const handlePageScroll = useCallback((event) => {
    const container = event.target;
    const { scrollTop, clientHeight, scrollHeight } = container;

    // When user scrolls near bottom of the page
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      // Load more transactions if available
      // Use isLoadingRef.current for synchronous check to prevent duplicate fetches on rapid scroll
      if (!isLoadingRef.current && hasMore && !authLoading) {
        // Set ref synchronously to prevent race conditions from rapid scroll events
        isLoadingRef.current = true;
        const nextPage = page + 1;
        Logger.debug('Loading more transactions', {
          nextPage,
          currentTransactions: transactions.length,
          scrollPosition: { scrollTop, clientHeight, scrollHeight }
        });
        setPage(nextPage);
        fetchTransactions(nextPage, true);
      }
    }
  }, [hasMore, authLoading, page, transactions.length, fetchTransactions]);

  // Attach scroll listener to the Layout's scrollable main container
  useEffect(() => {
    // Find the scrollable parent (Layout's main Box with overflow-y: auto)
    const scrollableParent = document.querySelector('main[class*="MuiBox"]') || 
                              document.querySelector('[style*="overflow-y: auto"]') ||
                              document.querySelector('[style*="overflow: auto"]');
    
    if (scrollableParent) {
      scrollableParent.addEventListener('scroll', handlePageScroll);
      return () => scrollableParent.removeEventListener('scroll', handlePageScroll);
    }
  }, [handlePageScroll]);

  // Removed unused handleTransactionListScroll

  // Removed unused handleFilterReset

  const handleColumnSort = (field) => {
    setSortConfig(prevConfig => ({
      field,
      direction: 
        prevConfig.field === field && prevConfig.direction === 'desc' 
          ? 'asc' 
          : 'desc'
    }));
  };

  const SortableColumnHeader = ({ field, label, width }) => (
    <Box 
      onClick={() => handleColumnSort(field)}
      sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: field === 'description' ? 'flex-start' : 'flex-end',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        width: width || 'auto',
        '&:hover': (theme) => ({
          color: theme.palette.primary.main
        })
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {sortConfig.field === field && (
        sortConfig.direction === 'desc' ? <ArrowDownIcon sx={{ fontSize: 16 }} /> : <ArrowUpIcon sx={{ fontSize: 16 }} />
      )}
    </Box>
  );

  // const uniqueCategories = [...new Set(filteredTransactions.map(t => t.category).filter(Boolean))];

  const handleDownload = () => {
    Logger.info('Starting transaction download', {
      filters: appliedFilters,
      sortConfig,
      transactionCount: filteredTransactions.length
    });
    
    if (filteredTransactions.length === 0) {
      showError('No transactions to download');
      return;
    }
    
    try {
      // Generate CSV content from filtered transactions
      const csvContent = transactionsToCSV(filteredTransactions, { accounts });
      
      // Generate filename based on date and filters
      const filename = generateCSVFilename(appliedFilters);
      
      // Trigger download and check result
      const downloadSucceeded = downloadCSV(csvContent, filename);
      
      if (!downloadSucceeded) {
        throw new Error('Download failed');
      }
      
      Logger.info('Transaction download complete', {
        transactionCount: filteredTransactions.length,
        filename,
        sortConfig
      });
      
      showSuccess(`Downloaded ${filteredTransactions.length} transaction${filteredTransactions.length === 1 ? '' : 's'}`);
    } catch (error) {
      Logger.error('Failed to download transactions', error);
      showError('Failed to download transactions. Please try again.');
    }
  };

  const groupTransactionsByMonth = (transactions) => {
    const groups = {};
    
    // Helper function to parse date correctly
    // Dates are stored in UTC but represent a calendar date in the user's configured timezone
    // Extract the date part (YYYY-MM-DD) and parse as local date to preserve the calendar date
    // This avoids timezone conversion issues when browser timezone differs from user's configured timezone
    const parseTransactionDate = (transaction) => {
      const dateValue = transaction.authorizedDate || transaction.date;
      if (!dateValue) return null;
      
      // Extract date part (YYYY-MM-DD) from ISO string to preserve calendar date
      const dateStr = typeof dateValue === 'string' 
        ? dateValue.split('T')[0]  // Extract YYYY-MM-DD part
        : dateValue.toISOString().split('T')[0];
      
      // Parse as local date (not UTC) to preserve the calendar date value
      // This ensures getFullYear() and getMonth() return the correct year/month
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    
    transactions.forEach(transaction => {
      const date = parseTransactionDate(transaction);
      if (!date) return; // Skip transactions without dates
      
      // Use getFullYear() and getMonth() on the local date (correct since we parsed as local)
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      if (!groups[key]) {
        groups[key] = {
          monthYear: new Date(date.getFullYear(), date.getMonth(), 1),
          transactions: []
        };
      }
      groups[key].transactions.push(transaction);
    });
    
    // Sort months descending (newest first)
    const sortedGroups = Object.values(groups).sort((a, b) => b.monthYear - a.monthYear);
    
    // Sort transactions within each month group by date (descending - newest first)
    // Pre-calculate parsed dates to avoid redundant parsing in sort comparison
    sortedGroups.forEach(group => {
      const transactionsWithDate = group.transactions.map(txn => ({
        txn,
        parsedDate: parseTransactionDate(txn)
      }));
      
      // Sort by parsedDate descending (newest first)
      transactionsWithDate.sort((a, b) => {
        if (!a.parsedDate || !b.parsedDate) return 0;
        return b.parsedDate.getTime() - a.parsedDate.getTime();
      });
      
      // Map back to just transactions
      group.transactions = transactionsWithDate.map(item => item.txn);
    });
    
    return sortedGroups;
  };

  // Update the groupedTransactions to use filteredTransactions instead of transactions
  const groupedTransactions = groupTransactionsByMonth(filteredTransactions);

  const handleTransactionUpdate = (updatedTransaction) => {
    Logger.debug('Handling transaction update', {
      transactionId: updatedTransaction._id,
      isBulkUpdate: updatedTransaction.bulkUpdate,
      updatedCount: updatedTransaction.updatedCount,
      similarCriteria: updatedTransaction.similarCriteria
    });
    
    if (updatedTransaction.bulkUpdate && updatedTransaction.similarCriteria) {
      // For bulk updates, update all similar transactions in-place
      setTransactions(prevTransactions => 
        prevTransactions.map(transaction => {
          // Check if this transaction matches ALL the criteria for similar transactions
          const isMatch = (
            // If merchantName is provided, it must match exactly
            (!updatedTransaction.similarCriteria.merchantName || 
             transaction.merchantName === updatedTransaction.similarCriteria.merchantName) &&
            // If no merchantName but description is provided, it must match exactly
            (!updatedTransaction.similarCriteria.merchantName && 
             updatedTransaction.similarCriteria.description && 
             transaction.description === updatedTransaction.similarCriteria.description) &&
            // If merchantEntityId is provided, it must match exactly
            (!updatedTransaction.similarCriteria.merchantEntityId || 
             transaction.merchantEntityId === updatedTransaction.similarCriteria.merchantEntityId)
          );

          if (isMatch) {
            Logger.debug('Updating similar transaction', {
              transactionId: transaction._id,
              merchantName: transaction.merchantName,
              description: transaction.description,
              merchantEntityId: transaction.merchantEntityId
            });
            // Apply the same category info to matching transactions
            return {
              ...transaction,
              budget: updatedTransaction.budget,
              budgetCategory: updatedTransaction.budgetCategory
            };
          }
          return transaction;
        })
      );
    } else {
      // For single transaction updates, update just that transaction
      setTransactions(prevTransactions => 
        prevTransactions.map(transaction => 
          transaction._id === updatedTransaction._id ? updatedTransaction : transaction
        )
      );
    }
  };

  const AccountsSection = () => {
    if (accountsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} color="primary" />
        </Box>
      );
    }

    if (!accounts || accounts.length === 0) {
      return (
        <Box 
          sx={{ 
            py: 2,
            px: 3,
            bgcolor: 'action.hover',
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No accounts available
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Connect a bank account to see transactions
          </Typography>
        </Box>
      );
    }

    const groupedAccounts = accounts.reduce((groups, account) => {
      const institution = account.institutionName || 'Other';
      if (!groups[institution]) {
        groups[institution] = [];
      }
      groups[institution].push(account);
      return groups;
    }, {});

    return (
      <Stack spacing={2}>
        {Object.entries(groupedAccounts).map(([institution, institutionAccounts]) => (
          <Box 
            key={institution}
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 2,
              p: 2
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 1
            }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.95rem'
                }}
              >
                {institution}
              </Typography>
              <Switch
                size="small"
                checked={institutionAccounts.every(acc =>
                  !selectedAccounts.includes(acc.id)
                )}
                onChange={(e) => handleInstitutionToggle(
                  institutionAccounts,
                  institutionAccounts[0].institutionId,
                  e.target.checked
                )}
              />
            </Box>
            <Stack spacing={1}>
              {institutionAccounts.map(account => (
                <Box
                  key={account.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pl: 2  // Add left padding to indent account names
                  }}
                >
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                      }}
                    >
                      {account.name}
                    </Typography>
                    {account.mask && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ opacity: 0.8 }}
                      >
                        ••••{account.mask}
                      </Typography>
                    )}
                  </Box>
                  <Switch
                    size="small"
                    checked={!selectedAccounts.includes(account.id)}
                    onChange={(e) => handleAccountToggle(
                      account,
                      institutionAccounts,
                      e.target.checked
                    )}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  // Add an effect to save filters when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(appliedFilters));
    } catch (e) {
      Logger.error('Failed to save filters to localStorage', e);
    }
  }, [appliedFilters]);

  // Add an effect to sync dialog state with applied filters when dialog opens
  useEffect(() => {
    if (filterDialogOpen) {
      setDateFilter(appliedFilters.dateFilter ?? 'all');
      setAmountFilter(appliedFilters.amountFilter ?? 'all');
      setMinAmount(appliedFilters.minAmount ?? '');
      setMaxAmount(appliedFilters.maxAmount ?? '');
      setShowTransfers(
        typeof appliedFilters.showTransfers === 'boolean' ? appliedFilters.showTransfers : true
      );
      setShowUncategorized(
        typeof appliedFilters.showUncategorized === 'boolean' ? appliedFilters.showUncategorized : true
      );
      setSelectedAccounts(appliedFilters.selectedAccounts || []);
      setSelectedInstitutions(appliedFilters.selectedInstitutions || []);
    }
  }, [filterDialogOpen, appliedFilters]);


  // Removed legacy empty state messages and helper (unused)

  const handleApplyFilters = () => {
    const nextFilters = {
      dateFilter,
      amountFilter,
      minAmount,
      maxAmount,
      showTransfers,
      showUncategorized,
      selectedAccounts,
      selectedInstitutions
    };

    setIsLoading(true);
    setAppliedFilters(nextFilters);
    setFilterDialogOpen(false);
    setPage(1);
    fetchTransactions(1, false, nextFilters);
  };

  // Removed unused handleClearFilters

  // Removed unused handleSort

  // Removed unused handlers

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 3 }} ref={mainContainerRef}>
        {/* Alerts Section */}
        <Box sx={{ 
          mb: 4,
          position: 'relative',
          left: 0,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          minHeight: 160
        }}>
          {isChildUser && (
            <Box sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 0
            }}>
              <Typography 
                variant="h2"
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '48px', sm: '56px', md: '64px' },
                  lineHeight: 1,
                  color: (theme) => theme.palette.error.light,
                  opacity: 0.18,
                  textAlign: 'center'
                }}
              >
                {formatAllowance(allowance)}
              </Typography>
            </Box>
          )}
          <Box sx={{ 
            display: 'flex',
            overflowX: 'auto',
            width: '100%',
            pt: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            position: 'relative',
            zIndex: 1
          }}>
            {alertsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (enrichedAlerts.length > 0 ? (
              enrichedAlerts.map(alert => (
                <AlertCard
                  key={alert._id}
                  alert={alert}
                  onDismiss={dismiss}
                  setLegalConsentData={setLegalConsentData}
                  setLegalConsentModalOpen={setLegalConsentModalOpen}
                  setIsLoading={setIsLoading}
                  setCategoryReviewBudgetId={setCategoryReviewBudgetId}
                  setCategoryReviewCategoryIds={setCategoryReviewCategoryIds}
                  setCategoryReviewModalOpen={setCategoryReviewModalOpen}
                  setSelectedRecurringPaymentAlert={setSelectedRecurringPaymentAlert}
                  setRecurringPaymentEditModalOpen={setRecurringPaymentEditModalOpen}
                  setSelectedBudgetId={setSelectedBudgetId}
                  setBudgetReviewModalOpen={setBudgetReviewModalOpen}
                  setSelectedReimbursementAlert={setSelectedReimbursementAlert}
                  setReimbursementMatchModalOpen={setReimbursementMatchModalOpen}
                  setTransactionReviewIds={setTransactionReviewIds}
                  setTransactionReviewAlertId={setTransactionReviewAlertId}
                  setTransactionReviewModalOpen={setTransactionReviewModalOpen}
                  setTransactionReviewType={setTransactionReviewType}
                  setSelectedBillMatchAlert={setSelectedBillMatchAlert}
                  setBillMatchReviewModalOpen={setBillMatchReviewModalOpen}
                  onOpenTransaction={openLinkedTransaction}
                />
              ))
            ) : (
              <Box sx={{ width: '100%', py: 8 }} />
            ))}
          </Box>
        </Box>

        {/* Transaction List Section */}
        {!error && (
          <Box 
            ref={mainContainerRef}
            sx={{ 
              bgcolor: 'background.paper',
              borderRadius: '20px',
              mb: 1,
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100vh - 80px)',
              overflow: 'auto',
              scrollBehavior: 'smooth',
              maxWidth: '100%',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
            onScroll={handlePageScroll}
          >
            {/* Fixed Header - All filtering and search available in free tier */}
            <Box sx={{ 
              p: 3, 
              pb: 2,
              bgcolor: 'background.paper',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              position: 'sticky',
              top: 0,
              zIndex: 3
            }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs>
                  <Paper
                    elevation={0}
                    sx={{
                      p: '6px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'action.hover',
                      borderRadius: '9999px',
                      boxShadow: 'none',
                      '& .MuiInputBase-root': {
                        '&.Mui-focused': {
                          '& > input': {
                            caretColor: 'primary.main'
                          }
                        }
                      },
                      '&:focus-within': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        bgcolor: 'background.paper'
                      }
                    }}
                  >
                    <IconButton sx={{ p: '10px' }}>
                      <SearchIcon />
                    </IconButton>
                    <InputBase
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-input': {
                          '&::placeholder': {
                            color: 'text.secondary',
                            opacity: 1
                          }
                        }
                      }}
                      placeholder="Search by name, description, or amount"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Paper>
                </Grid>

                <Grid item>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<AddIcon />}
                      variant="contained"
                      size="small"
                      onClick={() => setAddExpenseModalOpen(true)}
                      sx={{ 
                        bgcolor: (theme) => theme.palette.primary.main,
                        '&:hover': {
                          bgcolor: (theme) => theme.palette.primary.dark
                        }
                      }}
                    >
                      Add Expense
                    </Button>
                    <Button
                      startIcon={<FilterIcon />}
                      variant="text"
                      size="small"
                      onClick={() => {
                        Logger.debug('Filter button clicked');
                        setFilterDialogOpen(true);
                      }}
                      sx={{ color: (theme) => theme.palette.primary.main }}
                    >
                      Filter
                    </Button>
                    <Button
                      startIcon={<DownloadIcon />}
                      variant="text"
                      size="small"
                      onClick={handleDownload}
                      sx={{ color: (theme) => theme.palette.primary.main }}
                    >
                      Download
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {/* Column Headers */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: 2,
                mt: 2
              }}>
                <SortableColumnHeader field="description" label="Description" />
                <SortableColumnHeader field="date" label="Date" width={100} />
                <SortableColumnHeader field="amount" label="Amount" width={100} />
              </Box>
            </Box>

            {/* Transaction List */}
            <Box sx={{ flex: 1 }}>
              {transactions.length > 0 ? (
                groupedTransactions.map(({ monthYear, transactions: monthTransactions }) => (
                  <Box key={monthYear.getTime()}>
                    {/* Month Header */}
                    <Box sx={{ 
                      px: 3,
                      py: 1.5,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      justifyContent: 'center',
                      position: 'sticky',
                      top: 'calc(72px + 48px)',
                      zIndex: 1
                    }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          letterSpacing: '0.01em'
                        }}
                      >
                        {monthYear.toLocaleDateString('en-US', { 
                          month: 'long',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    <TransactionList 
                      transactions={monthTransactions} 
                      onTransactionUpdate={handleTransactionUpdate}
                    />
                  </Box>
                ))
              ) : !isLoading ? (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ p: 3 }}>
                  No transactions found
                </Typography>
              ) : null}

              {isLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 2,
                    position: 'sticky',
                    bottom: 0,
                    background: (theme) =>
                      `linear-gradient(180deg, ${theme.palette.background.paper}00 0%, ${theme.palette.background.paper} 40%)`
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Container>

      {/* Filter Dialog - All filtering available in free tier */}
      <StyledDialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            p: 3
          }
        }}
      >
        <DialogTitle sx={{ p: 0, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="div">
              Filter Transactions
            </Typography>
            <IconButton
              onClick={() => setFilterDialogOpen(false)}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ 
          p: 0,
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',  // IE and Edge
          scrollbarWidth: 'none'     // Firefox
        }}>
          <Stack spacing={3}>
            {/* Amount Filter */}
            <Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                Amount Range
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <StyledTextField
                    fullWidth
                    label="Min Amount"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                    type="number"
                    placeholder="Enter min amount"
                    InputProps={{
                      startAdornment: (
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          $
                        </Typography>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StyledTextField
                    fullWidth
                    label="Max Amount"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                    type="number"
                    placeholder="Enter max amount"
                    InputProps={{
                      startAdornment: (
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          $
                        </Typography>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Date Filter */}
            <Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                Date Range
              </Typography>
              <FormControl fullWidth>
                <StyledSelect
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="any">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="year">This Year</MenuItem>
                </StyledSelect>
              </FormControl>
            </Box>

            <Divider />

            {/* Transaction Types Section */}
            <Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                Transaction Types
              </Typography>
              <Stack spacing={1}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    p: 2
                  }}
                >
                  <Typography variant="body2">
                    Show Transfers
                  </Typography>
                  <Switch
                    size="small"
                    checked={showTransfers}
                    onChange={(e) => setShowTransfers(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase': (theme) => ({
                        '&.Mui-checked': {
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08)
                          },
                          '& + .MuiSwitch-track': {
                            bgcolor: `${theme.palette.primary.main}40`
                          }
                        }
                      }),
                      '& .MuiSwitch-track': {
                        bgcolor: 'action.selected'
                      }
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    p: 2
                  }}
                >
                  <Typography variant="body2">
                    Show Only Uncategorized
                  </Typography>
                  <Switch
                    size="small"
                    checked={showUncategorized}
                    onChange={(e) => setShowUncategorized(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase': (theme) => ({
                        '&.Mui-checked': {
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08)
                          },
                          '& + .MuiSwitch-track': {
                            bgcolor: `${theme.palette.primary.main}40`
                          }
                        }
                      }),
                      '& .MuiSwitch-track': {
                        bgcolor: 'action.selected'
                      }
                    }}
                  />
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* Accounts & Institutions Section */}
            <Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                Accounts & Institutions
              </Typography>
              <AccountsSection />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 0, mt: 4 }}>
          <StyledButton variant="outlined" onClick={() => setFilterDialogOpen(false)}>
            Cancel
          </StyledButton>
          <StyledButton variant="contained" onClick={handleApplyFilters}>
            Apply Filters
          </StyledButton>
        </DialogActions>
      </StyledDialog>

      {/* Legal Consent Modal */}
      <StyledDialog
        open={legalConsentModalOpen}
        onClose={() => setLegalConsentModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper>
              <WarningIcon sx={{ color: 'warning.main' }} />
            </IconWrapper>
            <Typography variant="h6">Legal Update Required</Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please review our updated Terms of Service and Privacy Policy before continuing.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <StyledButton
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => {
                window.open('/terms', '_blank', 'noopener,noreferrer');
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              View Terms of Service
            </StyledButton>

            <StyledButton
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => {
                window.open('/privacy', '_blank', 'noopener,noreferrer');
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              View Privacy Policy
            </StyledButton>
          </Box>

          <Typography variant="body2" color="text.secondary">
            By continuing, you agree to our updated Terms of Service and Privacy Policy.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <StyledButton
            variant="outlined"
            onClick={() => setLegalConsentModalOpen(false)}
          >
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            onClick={handleAcceptLegalConsent}
            disabled={isLoading}
          >
            {isLoading ? 'Accepting...' : 'Accept & Continue'}
          </StyledButton>
        </DialogActions>
      </StyledDialog>

      {/* Category Review Modal (Issue #129) */}
      <CategoryReviewModal
        open={categoryReviewModalOpen}
        onClose={() => {
          setCategoryReviewModalOpen(false);
          setCategoryReviewBudgetId(null);
          setCategoryReviewCategoryIds([]);
        }}
        budgetId={categoryReviewBudgetId}
        allCategoryIds={categoryReviewCategoryIds}
      />
      <RecurringPaymentEditModal
        open={recurringPaymentEditModalOpen}
        onClose={() => {
          setRecurringPaymentEditModalOpen(false);
          setSelectedRecurringPaymentAlert(null);
        }}
        alert={selectedRecurringPaymentAlert}
        onSuccess={async (message) => {
          showSuccess(message);
          // Dismiss the alert after successful update
          if (selectedRecurringPaymentAlert?._id) {
            await dismiss([selectedRecurringPaymentAlert._id]);
          }
          setRecurringPaymentEditModalOpen(false);
          setSelectedRecurringPaymentAlert(null);
        }}
        onError={(error) => {
          showError(error);
        }}
      />
      <BudgetReviewModal
        open={budgetReviewModalOpen}
        onClose={() => {
          setBudgetReviewModalOpen(false);
          setSelectedBudgetId(null);
        }}
        budgetId={selectedBudgetId}
        onApproved={(budget) => {
          // Mark onboarding step as complete
          completeOnboardingStep('budget').catch((error) => {
            console.error('Error completing budget step:', error);
          });
          showSuccess('Budget approved successfully!');
        }}
        onCategoryReview={(budget) => {
          // Open category review modal for detailed editing
          if (budget?.categories) {
            setCategoryReviewBudgetId(budget._id);
            setCategoryReviewCategoryIds(budget.categories.map(cat => cat._id));
            setCategoryReviewModalOpen(true);
          } else {
            // Fallback to budgets page
            navigate('/budgets');
          }
        }}
      />

      {/* Add Expense Modal */}
      <AddExpenseModal
        open={addExpenseModalOpen}
        onClose={() => setAddExpenseModalOpen(false)}
        onSuccess={(message) => {
          showSuccess(message);
          // Refresh transactions
          fetchTransactions(1, true);
        }}
      />

      {/* Reimbursement Match Modal */}
      <ReimbursementMatchModal
        open={reimbursementMatchModalOpen}
        onClose={() => {
          setReimbursementMatchModalOpen(false);
          setSelectedReimbursementAlert(null);
        }}
        alert={selectedReimbursementAlert}
        onSuccess={async (message) => {
          showSuccess(message);
          // Dismiss the alert after successful match
          if (selectedReimbursementAlert?._id) {
            await dismiss([selectedReimbursementAlert._id]);
          }
          setReimbursementMatchModalOpen(false);
          setSelectedReimbursementAlert(null);
        }}
      />

      {/* Transaction Review Modal for Low-Confidence AI Categorizations and Family Attribution */}
      <TransactionReviewModal
        open={transactionReviewModalOpen}
        onClose={() => {
          setTransactionReviewModalOpen(false);
          setTransactionReviewIds([]);
          setTransactionReviewAlertId(null);
          setTransactionReviewType('low-confidence');
        }}
        transactionIds={transactionReviewIds}
        alertId={transactionReviewAlertId}
        reviewType={transactionReviewType}
        onAllReviewed={() => {
          // Refresh alerts after all transactions are reviewed
          fetchAlerts({ page: 1, limit: ITEMS_PER_PAGE, status: ['pending', 'read'] });
        }}
      />

      <TransactionDetailModal
        open={Boolean(linkedTransaction)}
        onClose={() => setLinkedTransaction(null)}
        transaction={linkedTransaction}
        onTransactionUpdate={handleTransactionUpdate}
      />

      <BillMatchReviewModal
        open={billMatchReviewModalOpen}
        onClose={() => {
          setBillMatchReviewModalOpen(false);
          setSelectedBillMatchAlert(null);
        }}
        alert={selectedBillMatchAlert}
        onResolved={async () => {
          await fetchAlerts({ page: 1, limit: ITEMS_PER_PAGE, status: ['pending', 'read'] });
          setBillMatchReviewModalOpen(false);
          setSelectedBillMatchAlert(null);
        }}
      />
    </Layout>
  );
};

export default Activity;
